import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';

import { AIRewrite, Icon, registerIcon, useElement, useI18n } from '@pega/cosmos-react-core';
import type { AIAction } from '@pega/cosmos-react-core';
import * as polarisIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/polaris.icon';

import ToolbarButton from '../../RichTextEditor/Toolbar/ToolbarButton';
import { extractLinkAndImg, restorePlaceholders } from '../utils/htmlPlaceholder';
import type { PlaceholderMap } from '../utils/htmlPlaceholder';

registerIcon(polarisIcon);

export interface AIRewriteButtonProps {
  editor: TiptapEditor;
  onRewriteClick: (
    originalText: string,
    rewriteAction: AIAction,
    additionalInstructions: string,
    onSuccess: (rewrittenText: string) => void
  ) => void;
  renderSuggestionEditor?: (props: {
    defaultValue?: string;
    disabled: boolean;
    readOnly: boolean;
    onGetContent: (getContent: () => string) => void;
    onContentChange: (hasContent: boolean) => void;
  }) => ReactNode;
}

const AIRewriteButton = ({
  editor,
  onRewriteClick,
  renderSuggestionEditor
}: AIRewriteButtonProps) => {
  const t = useI18n();
  const [buttonEl, setButtonEl] = useElement<HTMLElement>();
  const [showPopover, setShowPopover] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState<string | undefined>(undefined);
  const [hasSuggestionContent, setHasSuggestionContent] = useState(false);
  const getContentRef = useRef<(() => string) | undefined>(undefined);
  const generationTokenRef = useRef(0);
  const placeholderMapRef = useRef<PlaceholderMap>({});
  const userPlaceholdersRef = useRef<string[]>([]);

  const getHtmlContent = (): string => {
    return editor ? editor.getHTML().trim() : '';
  };

  const hasContent = (): boolean => {
    return editor ? editor.getText().trim() !== '' : false;
  };

  const handleGenerate = (rewriteAction: AIAction, additionalInstructions: string) => {
    const originalContent = getHtmlContent();
    const { placeholderHtml, placeholderMap, userPlaceholders } =
      extractLinkAndImg(originalContent);
    placeholderMapRef.current = placeholderMap;
    userPlaceholdersRef.current = userPlaceholders;
    generationTokenRef.current += 1;
    const token = generationTokenRef.current;
    setIsGenerating(true);
    setSuggestion(undefined);
    setHasSuggestionContent(false);

    onRewriteClick(
      placeholderHtml,
      rewriteAction,
      additionalInstructions || '',
      (rewrittenText: string) => {
        if (token !== generationTokenRef.current) return;
        setIsGenerating(false);
        const trimmedText = rewrittenText.trim();
        setSuggestion(trimmedText || undefined);
        setHasSuggestionContent(trimmedText.length > 0);
      }
    );
  };

  const handleSubmit = () => {
    const editedContent = getContentRef.current?.() ?? suggestion ?? '';
    const restoredContent = restorePlaceholders(
      editedContent,
      placeholderMapRef.current,
      userPlaceholdersRef.current
    );
    editor.chain().setContent(restoredContent).focus('end').run();
    setShowPopover(false);
    setSuggestion(undefined);
    setHasSuggestionContent(false);
    placeholderMapRef.current = {};
    userPlaceholdersRef.current = [];
  };

  const handleCancel = () => {
    generationTokenRef.current += 1;
    setShowPopover(false);
    setSuggestion(undefined);
    setIsGenerating(false);
    setHasSuggestionContent(false);
    placeholderMapRef.current = {};
    userPlaceholdersRef.current = [];
    buttonEl?.focus();
  };

  return (
    <>
      <ToolbarButton
        ref={setButtonEl}
        onClick={() => {
          setShowPopover(true);
        }}
        disabled={!hasContent()}
        tooltip={t('ai_rewrite_title')}
        label={t('ai_rewrite_title')}
      >
        <Icon name='polaris' />
      </ToolbarButton>
      {showPopover && buttonEl && (
        <AIRewrite
          target={buttonEl}
          isGenerating={isGenerating}
          suggestion={hasSuggestionContent ? suggestion : undefined}
          onGenerate={handleGenerate}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          suggestionEditor={
            renderSuggestionEditor
              ? renderSuggestionEditor({
                  defaultValue: suggestion,
                  disabled: isGenerating || !suggestion,
                  readOnly: false,
                  onGetContent: getContent => {
                    getContentRef.current = getContent;
                  },
                  onContentChange: setHasSuggestionContent
                })
              : undefined
          }
        />
      )}
    </>
  );
};

export default AIRewriteButton;
